import AddIcon from "@mui/icons-material/Add"
import CloseIcon from "@mui/icons-material/Close"
import ContentCopyIcon from "@mui/icons-material/ContentCopy"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import {
    Accordion, AccordionDetails, AccordionSummary, Autocomplete, Box, Button, Checkbox,
    FormControl, FormControlLabel, IconButton, InputAdornment, InputLabel, MenuItem, Select, Skeleton,
    TextField, Tooltip, Typography,
} from "@mui/material"
import { DatePicker } from "@mui/x-date-pickers"
import dayjs from "dayjs"
import { useEffect, useState } from "react"
import { Control, Controller, FieldArrayWithId, UseFormGetValues, useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { IOrder, IOrderMixEntry, IOrderRef, IPhysicalCount } from "./types"

export default function ClientInfoSection({
    control, getValues, refFields, appendRef, updateRef,
    physicalCounts, loadingPhysicalCounts,
    createPhysicalCount, onCreatePhysicalCountChange,
}: {
    control: Control<IOrder>
    getValues: UseFormGetValues<IOrder>
    refFields: FieldArrayWithId<IOrder, "order_references", "id">[]
    appendRef: (value: IOrderRef) => void
    updateRef: (index: number, value: IOrderRef) => void
    physicalCounts: IPhysicalCount[]
    loadingPhysicalCounts: boolean
    createPhysicalCount: boolean
    onCreatePhysicalCountChange: (v: boolean) => void
}) {
    const { t } = useTranslation()
    const activeRefs = refFields.map((f, i) => ({ ...f, _idx: i })).filter(f => !(f as unknown as IOrderRef)._destroy)
    const mixEntries = useWatch({ control, name: "order_mix_entries" }) as IOrderMixEntry[] | undefined
    const hasMixItem = (mixEntries ?? []).some(e => !e._destroy && !!e.item_id)
    const [copiedCode, setCopiedCode] = useState(false)

    const handleCopyOrderCode = async (code: string) => {
        if (!code) return
        try {
            await navigator.clipboard.writeText(code)
            setCopiedCode(true)
            setTimeout(() => setCopiedCode(false), 1500)
        } catch (err) {
            console.error("Failed to copy order code", err)
        }
    }

    useEffect(() => {
        if (!hasMixItem && createPhysicalCount) onCreatePhysicalCountChange(false)
    }, [hasMixItem])

    return (
        <Accordion defaultExpanded disableGutters variant="outlined" sx={{ mb: 2, "&:before": { display: "none" } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2 }}>
                <Typography sx={{ fontWeight: "bold" }}>{t("orders.form.clientInfo")}</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 2, pb: 2 }}>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr 1fr" }, gap: 2 }}>
                    <Controller control={control} name="order_code"
                        render={({ field }) => (
                            <TextField
                                {...field}
                                label={t("orders.form.code")}
                                size="small"
                                fullWidth
                                slotProps={{
                                    input: {
                                        readOnly: true,
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <Tooltip title={t(copiedCode ? "common.copied" : "common.copy")}>
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            edge="end"
                                                            onClick={() => handleCopyOrderCode(field.value as string)}
                                                            disabled={!field.value}
                                                        >
                                                            <ContentCopyIcon fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            </InputAdornment>
                                        )
                                    }
                                }}
                            />
                        )}
                    />
                    <Controller control={control} name="order_date"
                        render={({ field }) => (
                            <DatePicker
                                label={t("orders.form.date")}
                                value={field.value ? dayjs(field.value as unknown as string) : null}
                                onChange={val => field.onChange(val ? val.format("YYYY-MM-DD") : null)}
                                format="DD-MM-YYYY"
                                slotProps={{ textField: { size: "small", fullWidth: true } }}
                            />
                        )}
                    />
                    <Controller control={control} name="order_entry_date"
                        render={({ field }) => (
                            <DatePicker
                                label={t("orders.form.entryDate")}
                                value={field.value ? dayjs(field.value as unknown as string) : null}
                                onChange={val => field.onChange(val ? val.format("YYYY-MM-DD") : null)}
                                format="DD-MM-YYYY"
                                slotProps={{ textField: { size: "small", fullWidth: true } }}
                            />
                        )}
                    />
                    <Controller control={control} name="client_name"
                        render={({ field }) => <TextField {...field} label={t("orders.form.client")} size="small" fullWidth />}
                    />
                    <Controller control={control} name="project_name"
                        render={({ field }) => <TextField {...field} label={t("orders.form.project")} size="small" fullWidth />}
                    />
                    <Controller control={control} name="location"
                        render={({ field }) => <TextField {...field} label={t("orders.form.location")} size="small" fullWidth />}
                    />
                    <Controller control={control} name="city"
                        render={({ field }) => <TextField {...field} label={t("orders.form.city")} size="small" fullWidth />}
                    />
                    <Controller control={control} name="status"
                        render={({ field }) => (
                            <FormControl size="small" fullWidth>
                                <InputLabel>{t("orders.form.status")}</InputLabel>
                                <Select {...field} label={t("orders.form.status")}>
                                    <MenuItem value="active">{t("orders.statuses.active")}</MenuItem>
                                    <MenuItem value="inactive">{t("orders.statuses.inactive")}</MenuItem>
                                </Select>
                            </FormControl>
                        )}
                    />
                    <Box sx={{ gridColumn: { xs: "1 / -1", md: "span 2" } }}>
                        {loadingPhysicalCounts ? (
                            <Skeleton variant="rounded" height={40} />
                        ) : (
                            <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                                <Controller control={control} name="physical_count_id"
                                    render={({ field }) => (
                                        <Autocomplete
                                            options={physicalCounts}
                                            value={physicalCounts.find(pc => pc.ID === field.value) ?? null}
                                            isOptionEqualToValue={(op, v) => op.ID === v.ID}
                                            getOptionLabel={op => op.Name}
                                            onChange={(_, data) => field.onChange(data?.ID ?? null)}
                                            disabled={createPhysicalCount}
                                            renderInput={params => <TextField {...params} label={t("orders.form.physicalCount")} size="small" />}
                                            sx={{ flex: 1, minWidth: 200 }}
                                            size="small"
                                        />
                                    )}
                                />
                                <Tooltip title={hasMixItem ? "" : t("orders.form.physicalCountRequiresMix")}>
                                    <span>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={createPhysicalCount}
                                                    onChange={e => onCreatePhysicalCountChange(e.target.checked)}
                                                    disabled={!hasMixItem}
                                                    size="small"
                                                />
                                            }
                                            label={t("orders.form.createNewPhysicalCount")}
                                        />
                                    </span>
                                </Tooltip>
                            </Box>
                        )}
                    </Box>
                    <Box sx={{ gridColumn: { xs: "1 / -1", md: "span 2" }, display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center", minWidth: 0 }}>
                        {activeRefs.map(({ _idx: idx }) => (
                            <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                <Controller control={control} name={`order_references.${idx}.reference_number` as keyof IOrder}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            label={`Ref #${activeRefs.findIndex(r => r._idx === idx) + 1}`}
                                            size="small"
                                            type="number"
                                            sx={{ width: 120 }}
                                        />
                                    )}
                                />
                                <IconButton size="small" color="error" onClick={() => {
                                    const refs = getValues("order_references") as IOrderRef[]
                                    updateRef(idx, { ...refs[idx], _destroy: true })
                                }}>
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        ))}
                        <Button
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => appendRef({ id: 0, reference_number: 0, _destroy: false })}
                        >
                            {t("orders.form.addReference")}
                        </Button>
                    </Box>
                </Box>
            </AccordionDetails>
        </Accordion>
    )
}
